import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Json } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

/** Fetches first team account data (team accounts only; personal accounts removed). */
export function usePersonalAccountData(
  userId: string,
  partialAccount?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
    public_data?: Json;
  },
) {
  const client = useSupabase();
  const queryKey = ['account:data', userId];

  const queryFn = async () => {
    if (!userId) {
      return null;
    }

    const { data: membership } = await client
      .from('accounts_memberships')
      .select('account_id')
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    if (!membership?.account_id) {
      return null;
    }

    const response = await client
      .from('accounts')
      .select('id, name, picture_url, public_data')
      .eq('id', membership.account_id)
      .single();

    if (response.error) {
      throw response.error;
    }

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!userId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: partialAccount?.id
      ? {
          id: partialAccount.id,
          name: partialAccount.name,
          picture_url: partialAccount.picture_url,
          public_data: partialAccount.public_data,
        }
      : undefined,
  });
}

export function useRevalidatePersonalAccountDataQuery() {
  const queryClient = useQueryClient();

  return useCallback(
    (userId: string) =>
      queryClient.invalidateQueries({
        queryKey: ['account:data', userId],
      }),
    [queryClient],
  );
}
